import { ApolloLink } from '@apollo/client';
import type { DocumentNode, OperationDefinitionNode, ValueNode } from 'graphql';
import { EMPTY, Observable } from 'rxjs';

const DIRECTIVE_NAME = 'serialize';

/**
 * Pull a key from `context.serializationKey` (preferred) or from a
 * `@serialize(key: [...])` directive on the operation. Returns a possibly
 * rewritten operation with the directive stripped so the server doesn't reject
 * it as an unknown directive.
 */
function extractKey(operation: ApolloLink.Operation): {
  key?: string;
  operation: ApolloLink.Operation;
} {
  const ctx = operation.getContext() as { serializationKey?: string };
  if (ctx.serializationKey) {
    return { key: ctx.serializationKey, operation };
  }

  const query = operation.query as DocumentNode;
  const opDef = query.definitions.find(
    (d): d is OperationDefinitionNode => d.kind === 'OperationDefinition',
  );
  if (!opDef || !opDef.directives || opDef.directives.length === 0) {
    return { operation };
  }

  const directive = opDef.directives.find(
    (d) => d.name.value === DIRECTIVE_NAME,
  );
  if (!directive) {
    return { operation };
  }

  const keyArg = directive.arguments?.find((a) => a.name.value === 'key');
  let key: string | undefined;
  if (keyArg) {
    if (keyArg.value.kind === 'ListValue') {
      key = JSON.stringify(
        keyArg.value.values.map((v) =>
          valueForArgument(v, operation.variables),
        ),
      );
    }
  }

  // Strip the directive from the document so the server accepts it.
  const strippedQuery: DocumentNode = {
    ...query,
    definitions: query.definitions.map((d) => {
      if (d.kind !== 'OperationDefinition') return d;
      return {
        ...d,
        directives: (d.directives ?? []).filter((dir) => dir !== directive),
      };
    }),
  };

  operation.query = strippedQuery;
  return { key, operation };
}

function valueForArgument(
  value: ValueNode,
  variables: Record<string, unknown> | undefined,
): unknown {
  if (value.kind === 'Variable') {
    if (!variables || !(value.name.value in variables)) {
      throw new Error(
        `No value supplied for variable $${value.name.value} used in @${DIRECTIVE_NAME} key`,
      );
    }
    return variables[value.name.value];
  }
  if (value.kind === 'IntValue') return parseInt(value.value, 10);
  if (value.kind === 'FloatValue') return parseFloat(value.value);
  if (
    value.kind === 'StringValue' ||
    value.kind === 'BooleanValue' ||
    value.kind === 'EnumValue'
  ) {
    return value.value;
  }
  throw new Error(
    `Argument of type ${value.kind} is not allowed in @${DIRECTIVE_NAME} directive`,
  );
}

/**
 * RxJS-based replacement for `apollo-link-serialize`.
 *
 * Honours the `@serialize(key: [...])` directive on an operation OR a
 * `serializationKey` value in the operation context. Operations sharing a key
 * are queued so only one is in flight at a time. Operations without a key are
 * passed straight through.
 *
 * Returns an RxJS `Observable<ApolloLink.Result>` so it composes cleanly with
 * the rest of the Apollo Client v4 RxJS-based link chain (the original v3
 * `apollo-link-serialize` returns a zen-observable, which breaks `.pipe(...)`
 * usage in downstream wrapper links).
 */
export class RxSerializingLink extends ApolloLink {
  private queues = new Map<string, Promise<void>>();

  request(
    operation: ApolloLink.Operation,
    forward?: ApolloLink.ForwardFunction,
  ) {
    if (forward === undefined) {
      return EMPTY;
    }

    const { key, operation: op } = extractKey(operation);

    // No serialization requested — pass straight through, wrapped to keep an
    // RxJS Observable for downstream `.pipe(...)` consumers.
    if (!key) {
      return new Observable<any>((subscriber) => {
        let innerSub: { unsubscribe?: () => void } | null = null;
        try {
          innerSub = (forward(op) as any).subscribe({
            next: (data: any) => subscriber.next(data),
            error: (err: any) => subscriber.error(err),
            complete: () => subscriber.complete(),
          });
        } catch (err) {
          subscriber.error(err);
        }
        return () => {
          try {
            innerSub?.unsubscribe?.();
          } catch {
            /* noop */
          }
        };
      }) as any;
    }

    return new Observable<any>((subscriber) => {
      let cancelled = false;
      let innerSub: { unsubscribe?: () => void } | null = null;

      const previous = this.queues.get(key) ?? Promise.resolve();
      const release = new Promise<void>((resolve) => {
        previous.then(() => {
          if (cancelled) {
            resolve();
            return;
          }
          try {
            innerSub = (forward(op) as any).subscribe({
              next: (data: any) => subscriber.next(data),
              error: (err: any) => {
                resolve();
                subscriber.error(err);
              },
              complete: () => {
                resolve();
                subscriber.complete();
              },
            });
          } catch (err) {
            resolve();
            subscriber.error(err);
          }
        });
      });
      this.queues.set(key, release);

      return () => {
        cancelled = true;
        try {
          innerSub?.unsubscribe?.();
        } catch {
          /* noop */
        }
      };
    }) as any;
  }
}
